import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  formatCad,
  type InvoiceStatus,
  type InvoiceSummary,
  type InvoiceTotals,
  type InvoiceView,
} from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { useAuthStore } from "../../../core/stores/auth";
import { InvoiceDetailModal } from "../components/invoice-detail-modal";
import { InvoiceRow } from "../components/invoice-row";
import { InvoicingSettingsModal } from "../components/invoicing-settings-modal";
import { getInvoice, getInvoiceSummary, invoicesCsvUrl, listInvoices } from "../lib/api";
import { addMonths, formatMonth, startOfMonth } from "../lib/dates";

type TabKey = "a_encaisser" | "en_souffrance" | "payees" | "annulees" | "toutes";

/** Onglets : l'argent qui reste à rentrer d'abord, sans période ; le reste par période. */
const TABS: Array<{ key: TabKey; label: string; statuses: InvoiceStatus[]; periodless?: boolean }> =
  [
    {
      key: "a_encaisser",
      label: "À encaisser",
      statuses: ["en_attente", "en_souffrance"],
      periodless: true,
    },
    { key: "en_souffrance", label: "En souffrance", statuses: ["en_souffrance"], periodless: true },
    { key: "payees", label: "Payées", statuses: ["payee"] },
    { key: "annulees", label: "Annulées", statuses: ["annulee"] },
    {
      key: "toutes",
      label: "Toutes",
      statuses: ["en_attente", "en_souffrance", "payee", "annulee"],
    },
  ];

type Period = "mois" | "annee" | "tout";
const PERIODS: Array<{ key: Period; label: string }> = [
  { key: "mois", label: "Mois" },
  { key: "annee", label: "Année" },
  { key: "tout", label: "Tout" },
];

function parseCursor(value: string | null): Date {
  const m = value && /^(\d{4})-(\d{2})$/.exec(value);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, 1) : startOfMonth(new Date());
}

function cursorKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function bounds(period: Period, cursor: Date): { from?: string; to?: string; label: string } {
  if (period === "mois") {
    const from = startOfMonth(cursor);
    return {
      from: from.toISOString(),
      to: addMonths(from, 1).toISOString(),
      label: formatMonth(cursor),
    };
  }
  if (period === "annee") {
    const from = new Date(cursor.getFullYear(), 0, 1);
    const to = new Date(cursor.getFullYear() + 1, 0, 1);
    return { from: from.toISOString(), to: to.toISOString(), label: String(cursor.getFullYear()) };
  }
  return { label: "Toutes les périodes" };
}

function StatCard({
  label,
  totals,
  accent,
}: {
  label: string;
  totals: InvoiceTotals | null;
  accent?: boolean;
}) {
  return (
    <div className={`atelier-stat${accent ? " atelier-stat--accent" : ""}`}>
      <span className="atelier-stat__label">{label}</span>
      <span className="atelier-stat__value">{totals ? formatCad(totals.totalCents) : "—"}</span>
      <span className="atelier-stat__note">
        {totals
          ? `${totals.count} facture${totals.count > 1 ? "s" : ""} · TPS ${formatCad(totals.gstCents)} · TVQ ${formatCad(totals.qstCents)}`
          : " "}
      </span>
    </div>
  );
}

/**
 * Registre central des factures : tableau de bord (jour, mois, année, encours),
 * onglets par statut, période, recherche, rangées qui ouvrent la fiche de la
 * facture. Exports CSV, rapport de taxes et réglages (admin) en tête.
 */
export function FacturesPage() {
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.find((t) => t.key === searchParams.get("onglet")) ?? TABS[0]!;
  const period = (PERIODS.find((p) => p.key === searchParams.get("periode"))?.key ??
    "mois") as Period;
  const cursor = useMemo(() => parseCursor(searchParams.get("mois")), [searchParams]);
  const urlQuery = searchParams.get("q") ?? "";
  const selectedId = searchParams.get("facture");

  const [query, setQuery] = useState(urlQuery);
  const [debounced, setDebounced] = useState(urlQuery.trim());
  const [items, setItems] = useState<InvoiceView[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [summary, setSummary] = useState<InvoiceSummary | null>(null);
  const [selected, setSelected] = useState<InvoiceView | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    if (debounced !== urlQuery.trim()) setParam("q", debounced || null);
  }, [debounced, urlQuery, setParam]);

  const range = bounds(period, cursor);
  const periodless = Boolean(tab.periodless);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listInvoices({
      status: tab.statuses,
      from: periodless ? undefined : range.from,
      to: periodless ? undefined : range.to,
      q: debounced || undefined,
      limit: 500,
    })
      .then((list) => {
        if (cancelled) return;
        setItems(list);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, periodless, range.from, range.to, debounced, reloadKey]);

  useEffect(() => {
    getInvoiceSummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [reloadKey]);

  // La facture ouverte vient de la liste, sinon de l'API (lien direct).
  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    const inList = items.find((i) => i.id === selectedId);
    if (inList) {
      setSelected(inList);
      return;
    }
    let cancelled = false;
    getInvoice(selectedId)
      .then((inv) => {
        if (!cancelled) setSelected(inv);
      })
      .catch(() => {
        if (!cancelled) setParam("facture", null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, items, setParam]);

  const step = (direction: -1 | 1) => {
    const next =
      period === "annee"
        ? new Date(cursor.getFullYear() + direction, cursor.getMonth(), 1)
        : addMonths(cursor, direction);
    setParam("mois", cursorKey(next));
  };

  const onInvoiceChange = (inv: InvoiceView) => {
    setSelected(inv);
    setItems((list) => list.map((i) => (i.id === inv.id ? inv : i)));
    setReloadKey((k) => k + 1);
  };

  const dueTotal = useMemo(
    () => items.filter((i) => i.status !== "annulee").reduce((s, i) => s + i.dueCents, 0),
    [items],
  );

  return (
    <div className="page factures">
      <PageHeader
        title="Factures"
        subtitle="Toutes les factures émises, ce qui reste à encaisser, TPS et TVQ."
        actions={
          <>
            <Button
              variant="ghost"
              icon={<Icon name="coins" size={18} />}
              onClick={() => navigate("/factures/taxes")}
            >
              Taxes à remettre
            </Button>
            <Button
              variant="ghost"
              icon={<Icon name="file" size={18} />}
              onClick={() =>
                window.open(
                  invoicesCsvUrl({
                    status: tab.statuses,
                    from: periodless ? undefined : range.from,
                    to: periodless ? undefined : range.to,
                    q: debounced || undefined,
                  }),
                  "_blank",
                )
              }
            >
              Exporter CSV
            </Button>
            {isAdmin ? (
              <Button
                variant="ghost"
                icon={<Icon name="gear" size={18} />}
                onClick={() => setSettingsOpen(true)}
              >
                Réglages
              </Button>
            ) : null}
          </>
        }
      />

      <div className="factures__stats">
        <StatCard label="Aujourd'hui" totals={summary?.today ?? null} />
        <StatCard label="Ce mois-ci" totals={summary?.month ?? null} />
        <StatCard label="Cette année" totals={summary?.year ?? null} />
        <div
          className={`atelier-stat atelier-stat--accent${
            summary && summary.outstanding.overdueCount > 0 ? " atelier-stat--late" : ""
          }`}
        >
          <span className="atelier-stat__label">À encaisser</span>
          <span className="atelier-stat__value">
            {summary ? formatCad(summary.outstanding.dueCents) : "—"}
          </span>
          <span className="atelier-stat__note">
            {summary
              ? `${summary.outstanding.count} facture${summary.outstanding.count > 1 ? "s" : ""}${
                  summary.outstanding.overdueCount > 0
                    ? ` · ${summary.outstanding.overdueCount} en souffrance (${formatCad(summary.outstanding.overdueCents)})`
                    : " · rien en souffrance"
                }`
              : " "}
          </span>
        </div>
      </div>

      <div className="chips chips--tabs" role="tablist" aria-label="Statut">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab.key === t.key}
            className={`chips__chip${tab.key === t.key ? " is-active" : ""}`}
            onClick={() => setParam("onglet", t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="atelier-toolbar">
        <div className="atelier-toolbar__search">
          <Input
            type="search"
            aria-label="Rechercher"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Numéro, client, véhicule…"
            enterKeyHint="search"
          />
        </div>
        {!periodless ? (
          <div className="factures__period">
            <div className="segmented" role="group" aria-label="Période">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  aria-pressed={period === p.key}
                  className={`segmented__btn${period === p.key ? " is-active" : ""}`}
                  onClick={() => setParam("periode", p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {period !== "tout" ? (
              <span className="factures__nav">
                <Button variant="ghost" size="sm" aria-label="Précédent" onClick={() => step(-1)}>
                  <Icon name="chevron-left" size={18} />
                </Button>
                <span className="factures__nav-label">{range.label}</span>
                <Button variant="ghost" size="sm" aria-label="Suivant" onClick={() => step(1)}>
                  <Icon name="chevron-right" size={18} />
                </Button>
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="atelier-toolbar__spacer" />
        <span className="atelier-toolbar__count">
          {loading
            ? "…"
            : `${items.length} facture${items.length > 1 ? "s" : ""}${
                dueTotal > 0 ? ` · ${formatCad(dueTotal)} dû` : ""
              }`}
        </span>
      </div>

      {!loading && items.length === 0 ? (
        <p className="empty">
          {failed
            ? "Registre indisponible — réessayez dans un instant."
            : debounced
              ? "Aucune facture ne correspond à la recherche."
              : tab.key === "a_encaisser"
                ? "Rien à encaisser : toutes les factures sont payées."
                : tab.key === "en_souffrance"
                  ? "Aucune facture en souffrance."
                  : "Aucune facture pour cette période."}
        </p>
      ) : loading && items.length === 0 ? (
        <p className="empty">Chargement…</p>
      ) : (
        <div className="job-list" role="list">
          {items.map((inv) => (
            <InvoiceRow key={inv.id} invoice={inv} onOpen={(i) => setParam("facture", i.id)} />
          ))}
        </div>
      )}

      <InvoiceDetailModal
        invoice={selected}
        onClose={() => setParam("facture", null)}
        onChange={onInvoiceChange}
      />
      <InvoicingSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
}
